import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function RemittanceTab() {
  const [remittances, setRemittances] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [showExceptions, setShowExceptions] = useState(false);
  const [exceptions, setExceptions] = useState([]);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rR, bR] = await Promise.all([
        api.get('/rcm/remittance/list'),
        api.get('/rcm/era/batches'),
      ]);
      setRemittances(rR.data.remittances || []);
      setBatches(bR.data.batches || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (remId) => {
    setSelected(remId); setSelectedBatch(null);
    try { const r = await api.get(`/rcm/remittance/${remId}`); setDetail(r.data); }
    catch { setDetail(null); }
  };

  const openBatchDetail = async (batchId) => {
    setSelectedBatch(batchId); setSelected(null);
    try { const r = await api.get(`/rcm/era/batches/${batchId}`); setBatchDetail(r.data); }
    catch { setBatchDetail(null); }
  };

  const loadExceptions = async () => {
    try { const r = await api.get('/rcm/era/exceptions?resolved=false'); setExceptions(r.data.exceptions || []); }
    catch { setExceptions([]); }
  };

  const toggleExceptions = () => { if (!showExceptions) loadExceptions(); setShowExceptions(!showExceptions); };

  const importDemoERA = async () => {
    setImporting(true);
    try { await api.post('/rcm/era/import-demo', { count: 5 }); load(); }
    catch(e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setImporting(false); }
  };

  const importDemoRemittance = async () => {
    setImporting(true);
    try { await api.post('/rcm/remittance/import-demo', { count: 10 }); load(); }
    catch(e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setImporting(false); }
  };

  const resolveException = async (excId, action) => {
    try { await api.post(`/rcm/era/exceptions/${excId}/resolve`, { action, notes: '' }); loadExceptions(); if (selectedBatch) openBatchDetail(selectedBatch); }
    catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading && !batches.length) return <div className="p-4 text-sm text-muted-foreground">Loading remittance...</div>;

  // Status icon helper
  const StatusIcon = ({ status }) => {
    if (status === 'parsing' || status === 'uploading') return <RotateCw className="h-3 w-3 text-blue-500 animate-spin" />;
    if (status === 'parsed' || status === 'processed') return <CheckCircle2 className="h-3 w-3 text-emerald-500" />;
    if (status === 'partial_exception') return <AlertTriangle className="h-3 w-3 text-amber-500" />;
    if (status === 'failed') return <XCircle className="h-3 w-3 text-red-500" />;
    return <Clock className="h-3 w-3 text-muted-foreground" />;
  };

  return (
    <div className="space-y-4" data-testid="remittance-tab">
      {/* ═══ PRIMARY ENTRY POINT: Import ERA / EOB ═══ */}
      <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-4 flex items-center justify-between" data-testid="remittance-entry-point">
        <div>
          <h3 className="text-sm font-semibold text-teal-900">Import ERA / EOB</h3>
          <p className="text-xs text-teal-700 mt-0.5">Upload or fetch remittance files. Batches appear immediately after import.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700" onClick={importDemoERA} disabled={importing} data-testid="import-era-btn">
            {importing ? <><RotateCw className="h-3 w-3 mr-1.5 animate-spin" />Importing...</> : <><Package className="h-3 w-3 mr-1.5" />Import ERA (Demo)</>}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs border-teal-400 text-teal-700" onClick={importDemoRemittance} disabled={importing} data-testid="import-eob-btn">
            <FileText className="h-3 w-3 mr-1.5" />Import EOB (Demo)
          </Button>
        </div>
      </div>

      {/* ═══ ERA BATCH LIST ═══ */}
      <Card data-testid="era-batch-list">
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs flex items-center gap-1.5">
            <Package className="h-3 w-3 text-teal-600" />ERA Import Batches ({batches.length})
          </CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={toggleExceptions} data-testid="toggle-exceptions">
              <AlertTriangle className="h-3 w-3 mr-1" />{showExceptions ? 'Hide' : 'Show'} Exceptions ({exceptions.length || '?'})
            </Button>
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {batches.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No batches imported yet. Use the Import button above to get started.</p>
          ) : (
            <table className="w-full text-xs" data-testid="batch-table">
              <thead><tr className="border-b text-left">
                <th className="py-1.5 font-medium text-muted-foreground w-6"></th>
                <th className="py-1.5 font-medium text-muted-foreground">Source</th>
                <th className="py-1.5 font-medium text-muted-foreground">Status</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">Claims</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">Lines</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">Amount</th>
                <th className="py-1.5 font-medium text-muted-foreground">Confidence</th>
                <th className="py-1.5 font-medium text-muted-foreground text-right">Exc.</th>
                <th className="py-1.5 font-medium text-muted-foreground">Date</th>
                <th></th>
              </tr></thead>
              <tbody>{batches.map((b) => {
                const cd = b.confidence_distribution || {};
                return (
                  <tr key={b.id} className={`border-b hover:bg-muted/30 cursor-pointer ${b.import_status === 'failed' ? 'bg-red-50/30' : ''}`} onClick={() => openBatchDetail(b.id)} data-testid={`batch-row-${b.id?.slice(0,8)}`}>
                    <td className="py-1.5"><StatusIcon status={b.import_status} /></td>
                    <td className="py-1.5"><Badge variant="outline" className="text-[10px]">{b.source_type}</Badge></td>
                    <td className="py-1.5"><StatusChip status={b.import_status} size="xs" /></td>
                    <td className="py-1.5 text-right">{b.total_claims_detected}</td>
                    <td className="py-1.5 text-right">{b.total_lines_detected}</td>
                    <td className="py-1.5 text-right font-medium">{fmt(b.total_amount_reported)}</td>
                    <td className="py-1.5">
                      <div className="flex gap-1">
                        {cd.HIGH > 0 && <span className="text-[9px] bg-emerald-100 text-emerald-700 rounded px-1">{cd.HIGH}H</span>}
                        {cd.MEDIUM > 0 && <span className="text-[9px] bg-amber-100 text-amber-700 rounded px-1">{cd.MEDIUM}M</span>}
                        {cd.LOW > 0 && <span className="text-[9px] bg-red-100 text-red-700 rounded px-1">{cd.LOW}L</span>}
                        {cd.EXCEPTION > 0 && <span className="text-[9px] bg-gray-200 text-gray-700 rounded px-1">{cd.EXCEPTION}E</span>}
                      </div>
                    </td>
                    <td className="py-1.5 text-right">{b.exception_count > 0 ? <span className="text-red-600 font-medium">{b.exception_count}</span> : '—'}</td>
                    <td className="py-1.5 text-muted-foreground text-[10px]">{b.created_at?.slice(0,10)}</td>
                    <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ═══ EXCEPTION PANEL ═══ */}
      {showExceptions && (
        <Card className="border-red-200 bg-red-50/10" data-testid="exception-panel">
          <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <ShieldAlert className="h-3 w-3 text-red-600" />Import Exceptions ({exceptions.length})
            </CardTitle>
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={loadExceptions}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {exceptions.length === 0 ? <p className="text-xs text-muted-foreground py-2 text-center">No unresolved exceptions</p> : (
              <div className="space-y-2">
                {exceptions.map((exc) => (
                  <div key={exc.id} className="border rounded p-2 text-xs space-y-1" data-testid={`exception-${exc.id?.slice(0,8)}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px]">{exc.exception_type?.replace(/_/g,' ')}</Badge>
                        <span className={`text-[10px] font-medium ${exc.severity === 'critical' ? 'text-red-700' : exc.severity === 'high' ? 'text-orange-600' : 'text-amber-600'}`}>{exc.severity}</span>
                      </div>
                      <span className="text-muted-foreground text-[10px]">{exc.created_at ? new Date(exc.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-muted-foreground">{exc.suggested_resolution}</p>
                    {exc.raw_context_ref && (
                      <div className="bg-gray-50 rounded p-1.5 text-[10px] font-mono">
                        {Object.entries(exc.raw_context_ref).map(([k,v]) => <span key={k} className="mr-2">{k}: {String(v)}</span>)}
                      </div>
                    )}
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={() => resolveException(exc.id, 'dismiss')} data-testid={`exc-dismiss-${exc.id?.slice(0,8)}`}>Dismiss</Button>
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={() => resolveException(exc.id, 'escalate')} data-testid={`exc-escalate-${exc.id?.slice(0,8)}`}>Escalate</Button>
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={() => resolveException(exc.id, 'manual_match')} data-testid={`exc-match-${exc.id?.slice(0,8)}`}>Manual Match</Button>
                      <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={() => resolveException(exc.id, 'reprocess')} data-testid={`exc-followup-${exc.id?.slice(0,8)}`}>Follow-Up</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══ REMITTANCE RECORDS LIST ═══ */}
      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Remittance Records ({remittances.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {remittances.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No remittances yet</p> : (
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left">
              <th className="py-1.5 font-medium text-muted-foreground">Source</th>
              <th className="py-1.5 font-medium text-muted-foreground">Payer</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Amount</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Lines</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Matched</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Unmatched</th>
              <th className="py-1.5 font-medium text-muted-foreground">Date</th>
              <th></th>
            </tr></thead>
            <tbody>{remittances.map((r) => (
              <tr key={r.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(r.id)}>
                <td className="py-1.5"><Badge variant="outline" className="text-[10px]">{r.source_type}</Badge></td>
                <td className="py-1.5">{r.payer_name || '—'}</td>
                <td className="py-1.5 text-right font-medium">{fmt(r.total_amount)}</td>
                <td className="py-1.5 text-right">{r.line_count}</td>
                <td className="py-1.5 text-right text-emerald-600">{r.matched_count}</td>
                <td className="py-1.5 text-right text-red-600">{r.unmatched_count > 0 ? r.unmatched_count : '—'}</td>
                <td className="py-1.5 text-muted-foreground">{r.received_at?.slice(0,10)}</td>
                <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
              </tr>
            ))}</tbody>
          </table>)}
        </CardContent>
      </Card>

      {/* ═══ REMITTANCE DETAIL PANEL ═══ */}
      <SlidePanel open={!!selected} onClose={() => { setSelected(null); setDetail(null); }} title="Remittance Detail">
        {detail && (<>
          <Card><CardContent className="p-3 space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Payer</span><span className="font-medium">{detail.payer_name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Source</span><Badge variant="outline" className="text-[10px]">{detail.source_type}</Badge></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-medium">{fmt(detail.total_amount)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Lines</span><span>{detail.line_count} (matched: {detail.matched_count}, unmatched: {detail.unmatched_count})</span></div>
            {detail.batch_id && <div className="flex justify-between"><span className="text-muted-foreground">Batch</span><span className="font-mono text-[10px]">{detail.batch_id?.slice(0,12)}...</span></div>}
            {detail.era_confidence_level && <div className="flex justify-between"><span className="text-muted-foreground">ERA Confidence</span><span className={`font-medium ${detail.era_confidence_level === 'HIGH' ? 'text-emerald-600' : detail.era_confidence_level === 'MEDIUM' ? 'text-amber-600' : 'text-red-600'}`}>{detail.era_confidence_level} ({detail.era_overall_confidence}%)</span></div>}
          </CardContent></Card>

          <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Remittance Lines (Claim + Line Level)</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3 overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="remittance-lines">
                <thead><tr className="border-b text-left">
                  <th className="py-1 font-medium text-muted-foreground">#</th>
                  <th className="py-1 font-medium text-muted-foreground">CPT</th>
                  <th className="py-1 font-medium text-muted-foreground">Match</th>
                  <th className="py-1 font-medium text-muted-foreground">Confidence</th>
                  <th className="py-1 font-medium text-muted-foreground text-right">Billed</th>
                  <th className="py-1 font-medium text-muted-foreground text-right">Allowed</th>
                  <th className="py-1 font-medium text-muted-foreground text-right">Paid</th>
                  <th className="py-1 font-medium text-muted-foreground text-right">Adj.</th>
                  <th className="py-1 font-medium text-muted-foreground text-right">Variance</th>
                  <th className="py-1 font-medium text-muted-foreground">Denial</th>
                  <th className="py-1 font-medium text-muted-foreground">Posting</th>
                </tr></thead>
                <tbody>{detail.lines?.map((l) => (
                  <tr key={l.id || l.line_index} className={`border-b ${l.matching_status === 'unmatched' ? 'bg-red-50/50' : l.matching_confidence === 'low' ? 'bg-amber-50/30' : ''}`}>
                    <td className="py-1 text-muted-foreground">{l.line_index}</td>
                    <td className="py-1 font-mono">{l.raw_cpt_code || '—'}</td>
                    <td className="py-1"><StatusChip status={l.matching_status} size="xs" /></td>
                    <td className="py-1">
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${l.matching_confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : l.matching_confidence === 'medium' ? 'bg-amber-100 text-amber-700' : l.matching_confidence === 'low' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {(l.matching_confidence || 'none').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-1 text-right text-muted-foreground">{fmt(l.raw_billed_amount)}</td>
                    <td className="py-1 text-right text-muted-foreground">{fmt(l.raw_allowed_amount)}</td>
                    <td className="py-1 text-right font-medium">{fmt(l.raw_paid_amount)}</td>
                    <td className="py-1 text-right text-muted-foreground">{fmt(l.raw_adjustment_amount)}</td>
                    <td className={`py-1 text-right font-medium ${l.variance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{fmt(l.variance)}</td>
                    <td className="py-1">{l.raw_denial_remark_codes?.length > 0 ? l.raw_denial_remark_codes.map((c,i) => <Badge key={i} variant="destructive" className="text-[8px] mr-0.5 py-0">{c}</Badge>) : '—'}</td>
                    <td className="py-1"><StatusChip status={l.posting_status} size="xs" /></td>
                  </tr>
                ))}</tbody>
              </table>
          </CardContent></Card>

          <Card className="border-blue-200 bg-blue-50/20">
            <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-[10px] text-blue-700 uppercase tracking-wider">Raw Payer Facts (Immutable)</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3 text-xs text-muted-foreground">
              <p>These values come directly from the payer remittance and cannot be modified. No auto-posting occurs.</p>
            </CardContent>
          </Card>
        </>)}
      </SlidePanel>

      {/* ═══ BATCH DETAIL PANEL ═══ */}
      <SlidePanel open={!!selectedBatch} onClose={() => { setSelectedBatch(null); setBatchDetail(null); }} title="ERA Batch Detail">
        {batchDetail && (<>
          <Card><CardContent className="p-3 space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Source</span><Badge variant="outline" className="text-[10px]">{batchDetail.source_type}</Badge></div>
            <div className="flex justify-between items-center"><span className="text-muted-foreground">Status</span><div className="flex items-center gap-1.5"><StatusIcon status={batchDetail.import_status} /><StatusChip status={batchDetail.import_status} size="xs" /></div></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Claims</span><span className="font-medium">{batchDetail.total_claims_detected}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Lines</span><span className="font-medium">{batchDetail.total_lines_detected}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total Amount</span><span className="font-medium">{fmt(batchDetail.total_amount_reported)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Raw File Ref</span><span className="font-mono text-[10px]">{batchDetail.source_file_ref?.slice(0,16)}...</span></div>
          </CardContent></Card>

          {/* Confidence distribution chart */}
          {batchDetail.confidence_distribution && (
            <Card data-testid="confidence-distribution">
              <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Confidence Distribution</CardTitle></CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="flex gap-2">
                  {Object.entries(batchDetail.confidence_distribution).map(([k,v]) => (
                    <div key={k} className={`flex-1 text-center rounded p-2 ${k === 'HIGH' ? 'bg-emerald-50 border border-emerald-200' : k === 'MEDIUM' ? 'bg-amber-50 border border-amber-200' : k === 'LOW' ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className="text-lg font-bold">{v}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">{k}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Batch-linked remittance claims with line-level expansion */}
          {batchDetail.remittances?.length > 0 && (
            <Card data-testid="batch-claims">
              <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Claims in Batch ({batchDetail.remittances.length})</CardTitle></CardHeader>
              <CardContent className="px-3 pb-3 space-y-1.5">
                {batchDetail.remittances.map((rem) => (
                  <div key={rem.id} className="border rounded p-2 text-xs cursor-pointer hover:bg-muted/20" onClick={() => { setSelectedBatch(null); openDetail(rem.id); }} data-testid={`batch-claim-${rem.id?.slice(0,8)}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{rem.payer_name || rem.payer_id || '—'}</span>
                        {rem.era_confidence_level && (
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${rem.era_confidence_level === 'HIGH' ? 'bg-emerald-100 text-emerald-700' : rem.era_confidence_level === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {rem.era_confidence_level}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{fmt(rem.total_amount)}</span>
                        <span className="text-muted-foreground">{rem.line_count} lines</span>
                        <span className="text-emerald-600">{rem.matched_count}m</span>
                        {rem.unmatched_count > 0 && <span className="text-red-600">{rem.unmatched_count}u</span>}
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Batch Exceptions with resolution actions */}
          {batchDetail.exceptions?.length > 0 && (
            <Card className="border-red-200" data-testid="batch-exceptions">
              <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs text-red-700">Exceptions ({batchDetail.exceptions.length})</CardTitle></CardHeader>
              <CardContent className="px-3 pb-3 space-y-1.5">
                {batchDetail.exceptions.map((exc) => (
                  <div key={exc.id} className="border-l-2 border-red-300 pl-2 text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{exc.exception_type?.replace(/_/g,' ')}</span>
                      <span className={`text-[10px] ${exc.severity === 'critical' ? 'text-red-700' : 'text-amber-600'}`}>({exc.severity})</span>
                      {exc.resolved_at && <span className="text-emerald-600 text-[10px]">Resolved</span>}
                    </div>
                    <p className="text-muted-foreground">{exc.suggested_resolution}</p>
                    {!exc.resolved_at && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={(e) => { e.stopPropagation(); resolveException(exc.id, 'dismiss'); }}>Dismiss</Button>
                        <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={(e) => { e.stopPropagation(); resolveException(exc.id, 'manual_match'); }}>Manual Match</Button>
                        <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={(e) => { e.stopPropagation(); resolveException(exc.id, 'reprocess'); }}>Follow-Up</Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Navigate to Posting Review */}
          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setSelectedBatch(null); }} data-testid="go-posting-review">
              <ClipboardCheck className="h-3 w-3 mr-1.5" />Go to Posting Review
            </Button>
          </div>
        </>)}
      </SlidePanel>
    </div>
  );
}

// ═══════════════════════════════════════
// POSTING REVIEW TAB (Group 2)
// ═══════════════════════════════════════

export { RemittanceTab };
